import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { JobModel, Team } from '../../entities';
import { getPlayerWidgetIFrameCode } from '../../widgets/playerWidget/playerWidgetUtils';
import { notifySuccess } from '../../services/notifications';
import { Routes } from '../../routes';
import { input } from '..';
import { Theme, View } from '../../services/ui/utils';

export class StreamAssetsWindowState
  extends StoreNode {
  readonly nodeType = 'StreamAssetsWindow';

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'windowListener'>(this, {
      jobId: observable,
      teamId: observable,
      loading: observable,
      job: computed,
      team: computed,
      ingestUrl: computed,
      jobIframeEmbedCode: computed,
      iframeEmbedCode: computed,
      togglePublishVideo: action,
      windowListener: action,
      open: action,
      copyPageIframeCode: action,
      copyJobIframeCode: action,
      copyUrl: action,
      close: action,
    });

    this.window.listen(this.windowListener);
  }
  readonly window = new WindowState(this.store);
  readonly publicStream = input(this, {
    name: 'publicStream',
    label: () => this.publicStream.value ? 'Make Stream Private' : 'Make Stream Shareable',
    onChange: () => this.togglePublishVideo()
  });

  jobId!: string;
  teamId!: string;
  loading: boolean = false;

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get ingestUrl(): string | null {
    return this.job?.liveStream?.ingestUrl ?? null;
  }

  get jobIframeEmbedCode(): string {
    return getPlayerWidgetIFrameCode({ jobId: this.jobId })
  }

  get iframeEmbedCode(): string | null {
    if (!this.team) return null;

    const { displayOptions, id } = this.team;

    const view = displayOptions?.galleryDisplay as View ?? null;
    const theme = displayOptions?.theme as Theme ?? null;
    
    const libraryRoute =
      Routes.teamLibraryWidget(id, { view, theme });
      
    const embedRoute = window.location.origin + libraryRoute;

    const iframe = document.createElement('iframe');

    iframe.src = embedRoute;
    iframe.width = '1280';
    iframe.height = '720';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; clipboard-write; fullscreen');
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-downloads allow-orientation-lock');

    let code = iframe.outerHTML;

    // Reverse browser URL-encoding of ampersands in embed code
    code = code.replaceAll(/&amp;/gi, '&');
    return code;
  }

  async togglePublishVideo() {
    this.loading = true;

    await this.job?.handlePublishVideo();
    this.emit('jobUpdated');

    this.loading = false;
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  };

  open({ jobId, teamId }: { jobId: string, teamId: string }) {
    this.jobId = jobId;
    this.teamId = teamId;

    this.publicStream.loadValue(this.job?.isPublic);

    this.emit('open');
    this.dispatch('Overlays', 'openWindow', { name: 'StreamAssetsWindow' });
  }

  async copyPageIframeCode() {
    if (this.iframeEmbedCode) {
      await navigator.clipboard?.writeText(this.iframeEmbedCode);
      notifySuccess(this, 'Embed code copied to clipboard!');
    }
  }

  async copyJobIframeCode() {
    await navigator.clipboard?.writeText(this.jobIframeEmbedCode);
    notifySuccess(this, 'Embed code copied to clipboard!');
  }

  async copyUrl() {
    await navigator.clipboard?.writeText(this.ingestUrl!);
    notifySuccess(this, 'RTMP URL copied to clipboard!');
  }

  close() {
    this.window.close();
    this.dispatch('Overlays', 'closeWindow');
  }
}
